import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  AppBar,
  Toolbar,
  Button,
  Alert,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import SaasSettingsPage from './SaasSettingsPage';

const SaasDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editClient, setEditClient] = useState(null);
  const [subType, setSubType] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axiosPrivate.get('/saas/dashboard/clients');
        setClients(response.data.clients);
      } catch (err) {
        setError('Errore nel caricamento clienti SaaS');
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const handleLogout = () => {
    window.location.href = `${process.env.REACT_APP_BASE_PATH || ''}/login`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'warning';
      case 'suspended': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Attivo';
      case 'trial': return 'Trial';
      case 'suspended': return 'Sospeso';
      case 'cancelled': return 'Cancellato';
      default: return status;
    }
  };

  const handleEditClick = (client) => {
    setEditClient(client);
    setSubType(client.subscription_type || '');
    setSubStatus(client.subscription_status || '');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosPrivate.patch('/saas/subscription', {
        customer_code: editClient.customer_code,
        subscription_type: subType,
        subscription_status: subStatus
      });
      window.location.reload();
    } catch (err) {
      alert('Errore aggiornamento abbonamento');
    } finally {
      setSaving(false);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card 
      elevation={4}
      sx={{ 
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 8
        },
        minHeight: 140
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2 }}>
              {title}
            </Typography>
            <Typography variant="h2" component="h2" sx={{ fontWeight: 700, color: color, fontSize: '3rem' }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: color, opacity: 0.7 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress size={60} />
    </Box>
  );
  
  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  const totalClients = clients.length;
  const activeClients = clients.filter(c => c.subscription_status === 'active').length;
  const totalElderly = clients.reduce((sum, c) => sum + (c.metrics.elderly_count || 0), 0);
  const totalUsers = clients.reduce((sum, c) => sum + (c.metrics.users_count || 0), 0);

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: 'rgba(25, 118, 210, 0.95)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Dashboard SaaS - Gestionale Anziani
          </Typography>
          <Typography variant="body2" sx={{ mr: 3 }}>
            Admin: superadmin
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </Toolbar>
        
        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.2)' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-selected': {
                  color: 'white',
                }
              }
            }}
          >
            <Tab icon={<DashboardIcon />} label="Dashboard" />
            <Tab icon={<SettingsIcon />} label="Impostazioni Prezzi" />
          </Tabs>
        </Box>
      </AppBar>

      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        justifyContent: 'center',
        py: 4
      }}>
        {activeTab === 0 ? (
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
        
        {/* Statistiche generali */}
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 4, 
            fontWeight: 700, 
            color: 'white',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
          }}
        >
          Dashboard Amministrativa SaaS
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Totale Clienti" 
              value={totalClients} 
              icon={<BusinessIcon sx={{ fontSize: 60 }} />} 
              color="#1976d2" 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Clienti Attivi" 
              value={activeClients} 
              icon={<TrendingUpIcon sx={{ fontSize: 60 }} />} 
              color="#2e7d32" 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Totale Anziani" 
              value={totalElderly} 
              icon={<PeopleIcon sx={{ fontSize: 60 }} />} 
              color="#ed6c02" 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Totale Utenti" 
              value={totalUsers} 
              icon={<PeopleIcon sx={{ fontSize: 60 }} />} 
              color="#9c27b0" 
            />
          </Grid>
        </Grid>

        {/* Modifica abbonamento */}
        {editClient && (
          <Paper elevation={8} sx={{ 
            p: 4, 
            mb: 4, 
            bgcolor: 'rgba(255, 243, 224, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 152, 0, 0.8)' 
          }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#e65100' }}>
              Modifica abbonamento per: {editClient.company_name}
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={{
                  '& .MuiInputLabel-root': { color: '#666', fontWeight: 600 },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#ccc', borderWidth: 2 },
                    '&:hover fieldset': { borderColor: '#1976d2' },
                    '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 2 },
                    '& .MuiSelect-select': { color: '#333', fontWeight: 600 }
                  }
                }}>
                  <InputLabel>Tipo Abbonamento</InputLabel>
                  <Select value={subType} label="Tipo Abbonamento" onChange={e => setSubType(e.target.value)}>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="premium_plus">Premium Plus</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={{
                  '& .MuiInputLabel-root': { color: '#666', fontWeight: 600 },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#ccc', borderWidth: 2 },
                    '&:hover fieldset': { borderColor: '#1976d2' },
                    '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 2 },
                    '& .MuiSelect-select': { color: '#333', fontWeight: 600 }
                  }
                }}>
                  <InputLabel>Stato</InputLabel>
                  <Select value={subStatus} label="Stato" onChange={e => setSubStatus(e.target.value)}>
                    <MenuItem value="active">Attivo</MenuItem>
                    <MenuItem value="inactive">Disattivo</MenuItem>
                    <MenuItem value="pending">In attesa</MenuItem>
                    <MenuItem value="cancelled">Cancellato</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button 
                  variant="contained" 
                  fullWidth
                  size="large"
                  onClick={handleSave} 
                  disabled={saving}
                  sx={{ height: 56, fontSize: '1rem', fontWeight: 600 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Salva Modifiche'}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button 
                  variant="outlined" 
                  fullWidth
                  size="large"
                  onClick={() => setEditClient(null)}
                  sx={{ height: 56, fontSize: '1rem', fontWeight: 600 }}
                >
                  Annulla
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Titolo sezione clienti */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'center' }}>
          <BusinessIcon sx={{ mr: 2, fontSize: 48, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontSize: '2rem'
            }}
          >
            Elenco Clienti
          </Typography>
        </Box>

        {/* Lista clienti con schede */}
        <Grid container spacing={3}>
          {clients.map((client) => (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={client.customer_code}>
              <Card 
                elevation={6} 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'rgba(255,255,255,0.98)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: 10
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Header Card */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ mr: 1.5, color: '#1976d2', fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', flexGrow: 1, lineHeight: 1.3, color: '#1976d2' }}>
                      {client.company_name}
                    </Typography>
                  </Box>
                  
                  {/* Info contatto */}
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                      🏢 DATI AZIENDALI
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0.75, fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                      📧 {client.email}
                    </Typography>
                    {client.company_phone && (
                      <Typography variant="body1" sx={{ mb: 0.75, fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                        � {client.company_phone}
                      </Typography>
                    )}
                    {client.vat_number && (
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                        P.IVA: {client.vat_number}
                      </Typography>
                    )}
                    {client.fiscal_code && (
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                        C.F.: {client.fiscal_code}
                      </Typography>
                    )}
                    {client.sdi_code && (
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                        SDI: {client.sdi_code}
                      </Typography>
                    )}
                    {client.contact_person && (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                        👤 Contatto: {client.contact_person}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Sede Legale */}
                  {client.address && (
                    <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                        📍 SEDE LEGALE
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                        {client.address}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                        {client.postal_code} {client.city} {client.country && `(${client.country})`}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Abbonamento con Piano */}
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                      💳 ABBONAMENTO
                    </Typography>
                    {client.subscription && client.subscription.plan ? (
                      <>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: '0.95rem', fontWeight: 700, color: '#333' }}>
                          {client.subscription.plan.plan_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Chip 
                            label={client.subscription.billing_cycle === 'monthly' 
                              ? `€${client.subscription.plan.monthly_price}/mese`
                              : `€${client.subscription.plan.yearly_price}/anno`
                            }
                            color="primary" 
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.8rem', height: 24 }}
                          />
                          <Chip 
                            label={client.subscription.status || 'N/A'} 
                            color={client.subscription.status === 'active' ? 'success' : 'warning'} 
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.8rem', height: 24 }}
                          />
                          {client.subscription.auto_renew && (
                            <Chip 
                              label="🔄 Auto-Rinnovo" 
                              color="info" 
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.8rem', height: 24 }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                          Scadenza: {client.subscription.end_date 
                            ? new Date(client.subscription.end_date).toLocaleDateString('it-IT')
                            : 'N/A'
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                          Limiti: {client.subscription.plan.max_users} utenti, {client.subscription.plan.max_elderly} anziani
                        </Typography>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip 
                          label={client.subscription_type || 'N/A'} 
                          color="primary" 
                          size="medium"
                          sx={{ fontWeight: 700, fontSize: '0.85rem', height: 28 }}
                        />
                        <Chip 
                          label={client.subscription_status || 'N/A'} 
                          color={client.subscription_status === 'active' ? 'success' : 'warning'} 
                          size="medium"
                          sx={{ fontWeight: 700, fontSize: '0.85rem', height: 28 }}
                        />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Fatturazione */}
                  {client.billing && (
                    <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                        💰 FATTURAZIONE
                      </Typography>
                      {client.billing.pending_invoices_count > 0 ? (
                        <>
                          <Chip 
                            label={`⚠️ ${client.billing.pending_invoices_count} in sospeso`}
                            color="warning" 
                            size="small"
                            sx={{ mb: 1, fontWeight: 700, fontSize: '0.8rem' }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#d32f2f' }}>
                            €{parseFloat(client.billing.pending_amount || 0).toFixed(2)} da pagare
                          </Typography>
                        </>
                      ) : client.billing.total_paid > 0 ? (
                        <>
                          <Chip 
                            label="✅ In regola"
                            color="success" 
                            size="small"
                            sx={{ mb: 1, fontWeight: 700, fontSize: '0.8rem' }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#2e7d32' }}>
                            Tot: €{parseFloat(client.billing.total_paid || 0).toFixed(2)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                          Nessuna fattura
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Dati Tecnici */}
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                      ⚙️ DATI TECNICI
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                      💾 {client.database_name}
                    </Typography>
                  </Box>
                  
                  {/* Utente principale */}
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                      AMMINISTRATORE
                    </Typography>
                    {client.user ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5, color: '#333' }}>
                          {client.user.full_name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 600, color: '#333' }}>
                          📧 {client.user.email}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                          📞 {client.user.phone || 'Non specificato'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#666' }}>
                        Nessun amministratore trovato
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Metriche */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
                      STATISTICHE
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem' }}>
                            {client.metrics.elderly_count}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242' }}>
                            Anziani
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#9c27b0', fontSize: '1.75rem' }}>
                            {client.metrics.users_count}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242' }}>
                            Utenti
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem' }}>
                            {client.metrics.doctors_count}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242' }}>
                            Dottori
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#ed6c02', fontSize: '1.75rem' }}>
                            {client.metrics.family_members_count}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242' }}>
                            Parenti
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ display: 'block', fontSize: '0.85rem', mb: 0.5, fontWeight: 600, color: '#333' }}>
                        🕒 Ultimo: {client.metrics.last_login ? new Date(client.metrics.last_login).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Mai'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                        💾 {client.metrics.storage_used_gb ? client.metrics.storage_used_gb + ' GB' : '0 GB'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Date */}
                  <Box sx={{ pt: 1.5, borderTop: '2px solid #e0e0e0', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ display: 'block', fontSize: '0.85rem', mb: 0.5, fontWeight: 600, color: '#333' }}>
                      Creato: {client.created_at ? new Date(client.created_at).toLocaleDateString('it-IT') : '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                      Aggiornato: {client.updated_at ? new Date(client.updated_at).toLocaleDateString('it-IT') : '-'}
                    </Typography>
                  </Box>
                  
                  {/* Pulsante modifica */}
                  <Button 
                    variant="contained" 
                    fullWidth
                    size="large" 
                    sx={{ mt: 1.5, fontSize: '0.95rem', py: 1.5, fontWeight: 600 }} 
                    onClick={() => handleEditClick(client)}
                  >
                    Modifica Abbonamento
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Informazioni sistema */}
        <Paper elevation={8} sx={{ 
          mt: 5, 
          p: 4, 
          bgcolor: 'rgba(227, 242, 253, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(25, 118, 210, 0.5)' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <DashboardIcon sx={{ mr: 1.5, color: '#1976d2', fontSize: 36 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#0d47a1' }}>
              Sistema Multi-Tenant Attivo
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#424242', fontWeight: 600 }}>
                <strong style={{ color: '#1976d2' }}>Database Master:</strong> saas_management
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#424242', fontWeight: 600 }}>
                <strong style={{ color: '#1976d2' }}>Endpoint API:</strong> /saas/*
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#424242', fontWeight: 600 }}>
                <strong style={{ color: '#1976d2' }}>Clienti Totali:</strong> {clients.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#424242', fontWeight: 600 }}>
                <strong style={{ color: '#1976d2' }}>Database Isolati:</strong> {clients.length}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        </Container>
        ) : (
          // Tab 1: Impostazioni Prezzi
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
            <SaasSettingsPage />
          </Container>
        )}
      </Box>
    </Box>
  );
};

export default SaasDashboard;
